import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import QuickInterventionDialog from "@/components/QuickInterventionDialog";
import EditInterventionDialog from "@/components/EditInterventionDialog";
import LeaveEditDialog from "@/components/LeaveEditDialog";
import ChantierHoverPreview from "@/components/ChantierHoverPreview";
import DuplicateInterventionDialog from "@/components/DuplicateInterventionDialog";
import VoiceCloseButton from "@/components/VoiceCloseButton";
import { Plus, ArrowRight, Trash } from "@phosphor-icons/react";
import { haversineKm, fmtDistance } from "@/lib/geo";
import { isoDay, ivColor, leaveOnDay, LEAVE_LABEL, ivCoversDay, ivSpanDays, ivDayIndex, ivEndDay } from "./dateHelpers";
import { CaretLeft, CaretRight, X as XIcon } from "@phosphor-icons/react";
import { CategorieBadge } from "@/components/InterventionCategoryPicker";

// Distance between the chantiers of two consecutive interventions
function distBetweenIvs(prev, curr, chantiersMap) {
  if (!prev || !curr || !chantiersMap) return null;
  if (prev.chantier_id && curr.chantier_id && prev.chantier_id === curr.chantier_id) return null;
  const a = chantiersMap[prev.chantier_id];
  const b = chantiersMap[curr.chantier_id];
  if (!a || !b) return null;
  return haversineKm(a.latitude, a.longitude, b.latitude, b.longitude);
}

function DistanceRow({ km, testid }) {
  if (km == null) return null;
  return (
    <div className="flex items-center gap-1.5 pl-2 py-0.5 text-[10px] text-white/40 font-mono tabular-nums" data-testid={testid}>
      <ArrowRight size={10} weight="bold" className="text-white/30" />
      <span>{fmtDistance(km)}</span>
    </div>
  );
}

export default function PlanningView({ team, allTeam, days, interventions, isAdmin, currentUser, onChange, chantiersMap, clientsMap, leaves }) {
  const nav = useNavigate();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [selectedDayIdx, setSelectedDayIdx] = useState(() => {
    const idx = days.findIndex((d) => d.getTime() === today.getTime());
    return idx >= 0 ? idx : 0;
  });
  const selectedDay = days[selectedDayIdx];
  const selectedIso = selectedDay ? isoDay(selectedDay) : undefined;

  const [quickTarget, setQuickTarget] = useState(null);
  const [dragging, setDragging] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [editingIv, setEditingIv] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);
  const [extendingId, setExtendingId] = useState(null);
  const [dragSpan, setDragSpan] = useState(null); // { ivId, side, techId, from, to }
  const [duplicateTarget, setDuplicateTarget] = useState(null);

  const extendIv = async (iv, direction, forTechnicianId = null) => {
    if (extendingId) return;
    setExtendingId(iv.id);
    try {
      const payload = { direction };
      if (forTechnicianId) payload.for_technician_id = forTechnicianId;
      const r = await api.post(`/interventions/${iv.id}/extend`, payload);
      const isSplit = r.data?.split_from === iv.id;
      if (direction === "reset") toast.success("Ramené sur 1 seule journée");
      else if (direction === "shrink_left" || direction === "shrink_right") toast.success("Ce jour a été retiré");
      else if (isSplit) toast.success(direction === "right" ? "Jour ajouté pour cet équipier uniquement" : "Jour ajouté (précédent) pour cet équipier uniquement");
      else toast.success(direction === "right" ? "Étendu au jour suivant" : "Étendu au jour précédent");
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExtendingId(null); }
  };

  // Smart delete: on multi-day interventions we shrink the edge rather than
  // wipe the whole span. Middle days aren't safely removable from the agenda
  // (would split the intervention in two) — send the user to the edit dialog.
  const smartDelete = async (iv, dayIso) => {
    const spanDays = ivSpanDays(iv);
    const dayIdx = ivDayIndex(iv, dayIso);
    if (spanDays > 1) {
      if (dayIdx === 1) return extendIv(iv, "shrink_left");
      if (dayIdx === spanDays) return extendIv(iv, "shrink_right");
      toast.error("Retirer un jour au milieu d'une intervention casserait la continuité. Ouvre la fiche pour ajuster les dates.");
      return;
    }
    if (!window.confirm(`Supprimer « ${iv.title || "cette intervention"} » ?`)) return;
    try {
      await api.delete(`/interventions/${iv.id}`);
      toast.success("Intervention supprimée");
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onChevronPointerDown = (e, iv, side, rowTechId = null) => {
    // Left-click / primary touch only. Right-click / modifier-clicks bubble to normal onClick behaviour.
    if (!isAdmin || e.button === 2) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startIso = (iv.scheduled_date || "").slice(0, 10);
    const endIso = ivEndDay(iv);
    const originIso = side === "right" ? endIso : startIso;
    let currentTargetIso = originIso;
    // Row-scoped tech: click extends only this tech, drag falls back to whole
    // intervention for multi-tech (backend set-span applies to all).
    const scopeTechId = rowTechId || iv.technician_id;

    const onMove = (ev) => {
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = el?.closest?.("[data-day-iso][data-tech-id]");
      if (!cell) return;
      if (cell.dataset.techId !== scopeTechId) return;
      const dayIso = cell.dataset.dayIso;
      if (!dayIso || dayIso === currentTargetIso) return;
      currentTargetIso = dayIso;
      // Preview: highlight the span from origin to target
      const [from, to] = side === "right"
        ? [startIso, dayIso < startIso ? startIso : dayIso]
        : [dayIso > endIso ? endIso : dayIso, endIso];
      setDragSpan({ ivId: iv.id, side, techId: scopeTechId, from, to });
    };

    const onUp = async (ev) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      const dx = Math.abs(ev.clientX - startX);
      setDragSpan(null);
      // No drag (or landed back on origin) → treat as a normal chevron click (+1 day for this tech only).
      if (dx < 6 || currentTargetIso === originIso) {
        await extendIv(iv, side, rowTechId);
        return;
      }
      if (extendingId) return;
      setExtendingId(iv.id);
      try {
        const patch = side === "right"
          ? { end_day: currentTargetIso <= startIso ? null : currentTargetIso }
          : { start_day: currentTargetIso >= endIso ? endIso : currentTargetIso };
        await api.post(`/interventions/${iv.id}/set-span`, patch);
        const spanN = Math.abs(new Date(currentTargetIso) - new Date(originIso)) / 86400000;
        toast.success(`Intervention étalée sur ${spanN + 1} jour${spanN >= 1 ? "s" : ""}`);
        onChange?.();
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setExtendingId(null); }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const onDropReassign = async (techId, iso) => {
    if (!dragging) return;
    const iv = interventions.find((x) => x.id === dragging);
    setDragging(null); setDropTarget(null);
    if (!iv) return;
    if (iv.technician_id === techId && iv.scheduled_date.slice(0, 10) === iso) return;
    try {
      const time = (iv.scheduled_date || "").slice(11, 16) || "09:00";
      const newDate = `${iso}T${time}`;
      await api.patch(`/interventions/${iv.id}`, { technician_id: techId, scheduled_date: newDate });
      const targetTech = allTeam.find((u) => u.id === techId);
      toast.success(`Réassigné à ${targetTech?.name || "…"}`);
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const onDropReorder = async (techId, iso, dropIdx, sortedList) => {
    const draggedId = dragging;
    setDragging(null); setDropTarget(null);
    if (!draggedId) return;
    const dragged = interventions.find((x) => x.id === draggedId);
    if (!dragged) return;
    const sameDay = dragged.technician_id === techId && dragged.scheduled_date?.slice(0, 10) === iso;
    if (!sameDay) return onDropReassign(techId, iso);
    const remaining = sortedList.filter((x) => x.id !== draggedId);
    const currentIdx = sortedList.findIndex((x) => x.id === draggedId);
    let insertAt = dropIdx;
    if (currentIdx >= 0 && currentIdx < dropIdx) insertAt = dropIdx - 1;
    if (insertAt === currentIdx) return;
    remaining.splice(insertAt, 0, dragged);
    try {
      await Promise.all(remaining.map((it, i) => api.patch(`/interventions/${it.id}`, { sort_order: (i + 1) * 10 })));
      toast.success("Ordre mis à jour");
      onChange?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <>
      {/* MOBILE */}
      <div className="md:hidden space-y-3" data-testid="planning-mobile">
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
          {days.map((d, i) => {
            const isToday = d.getTime() === today.getTime();
            const isSel = i === selectedDayIdx;
            const dayIso = isoDay(d);
            const count = interventions.filter((x) => ivCoversDay(x, dayIso) && team.some((u) => u.id === x.technician_id || (x.assignee_ids || []).includes(u.id))).length;
            return (
              <button
                key={dayIso}
                onClick={() => setSelectedDayIdx(i)}
                className={`shrink-0 flex flex-col items-center px-3 py-2 rounded-2xl border transition-all ${isSel ? "bg-[#005CFF] border-[#005CFF] text-white shadow-lg shadow-[#005CFF]/20" : isToday ? "bg-[#005CFF]/10 border-[#005CFF]/30 text-[#7CB2FF]" : "bg-white/5 border-white/10 text-white/70 hover:bg-white/10"}`}
                data-testid={`planning-day-chip-${dayIso}`}
              >
                <span className="text-[9px] uppercase tracking-widest opacity-70">{d.toLocaleDateString("fr-FR", { weekday: "short" })}</span>
                <span className="font-display font-bold text-lg leading-none mt-0.5">{d.getDate()}</span>
                {count > 0 && (
                  <span className={`mt-1 text-[9px] font-mono px-1.5 py-0.5 rounded-full ${isSel ? "bg-white/25 text-white" : "bg-[#005CFF]/20 text-[#7CB2FF]"}`}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        <div className="space-y-2">
          {team.map((u) => {
            const iv = interventions
              .filter((x) => (x.technician_id === u.id || (x.assignee_ids || []).includes(u.id)) && ivCoversDay(x, selectedIso))
              .sort((a, b) => {
                const ta = (a.scheduled_date || "").slice(11, 16);
                const tb = (b.scheduled_date || "").slice(11, 16);
                if (ta !== tb) return ta.localeCompare(tb);
                return (a.sort_order || 0) - (b.sort_order || 0);
              });
            const techColor = u.color || "#005CFF";
            const initials = (u.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
            const rowKey = `row-${u.id}`;
            const isRowDropOver = dropTarget === rowKey;
            const leave = leaveOnDay(u.id, selectedIso, leaves);
            return (
              <div key={u.id} className={`rounded-2xl border overflow-hidden shadow-sm hover:shadow-md transition-shadow ${leave ? "bg-emerald-500/[0.08] border-emerald-500/30" : "bg-white/[0.03] border-white/10"}`} data-testid={`planning-tech-${u.id}`}>
                <div className={`flex items-center gap-2 px-3 py-2 border-b ${leave ? "border-emerald-500/20" : "border-white/5"}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0" style={{ background: techColor + "33", color: techColor }}>{initials}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{u.name}</div>
                    <div className={`text-[10px] truncate ${leave ? "text-emerald-300" : "text-white/40"}`}>
                      {leave
                        ? `${LEAVE_LABEL[leave.type] || "Congé"} · ${leave.start_date === leave.end_date ? "1 jour" : `${leave.start_date} → ${leave.end_date}`}`
                        : iv.length === 0 ? "Rien de prévu" : `${iv.length} intervention${iv.length > 1 ? "s" : ""}`}
                    </div>
                  </div>
                  {(isAdmin || currentUser?.id === u.id) && (
                    <button
                      onClick={() => setQuickTarget({ technicianId: u.id, technicianName: u.name, dateIso: selectedIso })}
                      className="w-7 h-7 rounded-full bg-[#005CFF]/20 border border-[#005CFF]/30 flex items-center justify-center text-[#7CB2FF] active:scale-95 hover:bg-[#005CFF]/30"
                      title={isAdmin ? "Nouvelle intervention" : "Nouvelle intervention pour moi"}
                      data-testid={`add-iv-mobile-${u.id}`}
                    >
                      <Plus size={12} weight="bold" />
                    </button>
                  )}
                </div>
                {iv.length > 0 && (
                  <div className="p-2 space-y-1" data-testid={`iv-list-${u.id}`}>
                    {iv.map((x, idx) => {
                      const gapKey = `gap-${u.id}-${idx}`;
                      const gapOver = dropTarget === gapKey;
                      const km = idx > 0 ? distBetweenIvs(iv[idx - 1], x, chantiersMap) : null;
                      const color = ivColor(x, chantiersMap, u);
                      return (
                        <div key={x.id}>
                          {isAdmin && (
                            <div
                              onDragOver={(e) => { if (dragging) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropTarget !== gapKey) setDropTarget(gapKey); } }}
                              onDragLeave={() => { if (dropTarget === gapKey) setDropTarget(null); }}
                              onDrop={(e) => { e.preventDefault(); onDropReorder(u.id, selectedIso, idx, iv); }}
                              className={`h-1.5 rounded-full transition-all ${gapOver ? "bg-[#005CFF] h-2" : "bg-transparent"}`}
                              data-testid={`drop-gap-${u.id}-${idx}`}
                            />
                          )}
                          {km != null && <DistanceRow km={km} testid={`dist-${u.id}-${idx}`} />}
                          <ChantierHoverPreview
                            chantier={x.chantier_id ? chantiersMap?.[x.chantier_id] : null}
                            client={x.chantier_id && chantiersMap?.[x.chantier_id]?.client_id ? clientsMap?.[chantiersMap[x.chantier_id].client_id] : null}
                            currentIv={x}
                            side="right"
                          >
                          <div
                              className={`relative group w-full rounded-lg flex items-center gap-2 transition-all ${dragging === x.id ? "opacity-40" : "hover:brightness-110"}`}
                              style={{ background: color + "1A", borderLeft: `3px solid ${color}` }}
                            >
                              {isAdmin && ivDayIndex(x, selectedIso) === 1 && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); extendIv(x, "left", u.id); }}
                                  disabled={extendingId === x.id}
                                  className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-black/40 border border-white/20 hover:bg-[#005CFF] hover:border-[#005CFF] flex items-center justify-center text-white/70 hover:text-white transition z-10 disabled:opacity-50"
                                  title="Étendre au jour précédent pour cet équipier"
                                  data-testid={`iv-extend-left-${x.id}`}
                                >
                                  <CaretLeft size={11} weight="bold" />
                                </button>
                              )}
                              {isAdmin && ivDayIndex(x, selectedIso) === ivSpanDays(x) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); extendIv(x, "right", u.id); }}
                                  disabled={extendingId === x.id}
                                  className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-black/40 border border-white/20 hover:bg-[#005CFF] hover:border-[#005CFF] flex items-center justify-center text-white/70 hover:text-white transition z-10 disabled:opacity-50"
                                  title="Étendre au jour suivant pour cet équipier"
                                  data-testid={`iv-extend-right-${x.id}`}
                                >
                                  <CaretRight size={11} weight="bold" />
                                </button>
                              )}
                              <button
                                draggable={isAdmin}
                                onDragStart={(e) => { if (!isAdmin) return; setDragging(x.id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", x.id); } catch (err) { /* Safari older versions may reject text/plain */ console.debug("[planning] setData failed:", err?.message || err); } }}
                                onDragEnd={() => { setDragging(null); setDropTarget(null); }}
                                onClick={() => setEditingIv(x)}
                                className={`flex-1 min-w-0 text-left px-2.5 py-2 flex items-center gap-2 active:scale-95 transition ${isAdmin ? "cursor-grab" : ""}`}
                                data-testid={`iv-mobile-${x.id}`}
                              >
                                <span className="text-[11px] font-mono tabular-nums text-white/70 shrink-0">
                                  {new Date(x.scheduled_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                                </span>
                                {x.type === "depannage" && <span className="text-[9px] bg-amber-500 text-black px-1.5 py-0.5 rounded-full font-bold shrink-0">SAV</span>}
                                <CategorieBadge value={x.categorie} size="xs" showLabel={false} />
                                <span className="text-xs truncate flex-1">{x.title}</span>
                                {ivSpanDays(x) > 1 && (
                                  <span
                                    className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-white/10 text-white/70 shrink-0"
                                    title={`Multi-jours (${(x.scheduled_date || "").slice(0,10)} → ${ivEndDay(x)})`}
                                  >
                                    J{ivDayIndex(x, selectedIso)}/{ivSpanDays(x)}
                                  </span>
                                )}
                                {x.status === "done" && <span className="text-[9px] text-emerald-400">✓</span>}
                              </button>
                              {isAdmin && ivSpanDays(x) > 1 && (ivDayIndex(x, selectedIso) === 1 || ivDayIndex(x, selectedIso) === ivSpanDays(x)) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); extendIv(x, ivDayIndex(x, selectedIso) === 1 ? "shrink_left" : "shrink_right"); }}
                                  disabled={extendingId === x.id}
                                  className="px-1.5 py-2 text-white/40 hover:text-red-400 active:scale-95 transition-colors"
                                  title="Retirer ce jour"
                                  data-testid={`iv-shrink-${x.id}-${selectedIso}`}
                                >
                                  <XIcon size={11} weight="bold" />
                                </button>
                              )}
                              {(isAdmin || currentUser?.id === u.id) && x.status !== "done" && x.status !== "cancelled" && (
                                <div className="pr-1 flex items-center">
                                  <VoiceCloseButton intervention={x} onApplied={onChange} size="sm" />
                                </div>
                              )}
                              {x.chantier_id && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); nav(`/chantiers/${x.chantier_id}`); }}
                                  className="pr-2 pl-1 py-2 text-white/50 hover:text-white active:scale-95 transition-colors"
                                  title="Ouvrir la fiche chantier"
                                  data-testid={`iv-mobile-open-ch-${x.id}`}
                                >
                                  <ArrowRight size={13} weight="bold" />
                                </button>
                              )}
                            </div>
                          </ChantierHoverPreview>
                        </div>
                      );
                    })}
                    {isAdmin && (
                      <div
                        onDragOver={(e) => { if (dragging) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropTarget !== `gap-${u.id}-${iv.length}`) setDropTarget(`gap-${u.id}-${iv.length}`); } }}
                        onDragLeave={() => { if (dropTarget === `gap-${u.id}-${iv.length}`) setDropTarget(null); }}
                        onDrop={(e) => { e.preventDefault(); onDropReorder(u.id, selectedIso, iv.length, iv); }}
                        className={`h-3 rounded-full transition-all ${dropTarget === `gap-${u.id}-${iv.length}` ? "bg-[#005CFF] h-2" : "bg-transparent"}`}
                      />
                    )}
                  </div>
                )}
                {iv.length === 0 && !leave && isAdmin && (
                  <div
                    onDragOver={(e) => { if (dragging) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropTarget !== rowKey) setDropTarget(rowKey); } }}
                    onDragLeave={() => { if (dropTarget === rowKey) setDropTarget(null); }}
                    onDrop={(e) => { e.preventDefault(); onDropReassign(u.id, selectedIso); }}
                    className={`p-6 text-center text-[10px] transition-colors ${isRowDropOver ? "bg-[#005CFF]/15 text-[#7CB2FF]" : "text-white/20"}`}
                  >
                    {isRowDropOver ? "Déposer ici" : "—"}
                  </div>
                )}
                {iv.length === 0 && leave && (
                  <button
                    onClick={() => isAdmin && setEditingLeave(leave)}
                    disabled={!isAdmin}
                    className="w-full p-4 flex items-center justify-between gap-2 hover:bg-emerald-500/[0.06] active:scale-[0.99] transition text-left disabled:cursor-default"
                    data-testid={`planning-leave-${u.id}-mobile`}
                  >
                    <div className="text-[11px] text-emerald-300/80 flex items-center gap-1.5 flex-1 min-w-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                      <span className="truncate">
                        En {(LEAVE_LABEL[leave.type] || "congé").toLowerCase()}
                        {leave.reason && <span className="text-emerald-300/50"> · « {leave.reason} »</span>}
                      </span>
                    </div>
                    {isAdmin && <span className="text-[9px] text-emerald-400/60 uppercase tracking-widest">Modifier</span>}
                  </button>
                )}
              </div>
            );
          })}
          {team.length === 0 && (
            <div className="p-8 text-center text-white/40 text-sm">Aucun technicien{" "}<span className="text-white/30">(essaie de changer le filtre sous-équipe)</span></div>
          )}
        </div>
      </div>

      {/* DESKTOP weekly grid */}
      <div className="hidden md:block rounded-3xl bg-gradient-to-br from-white/[0.05] via-white/[0.02] to-transparent border border-white/10 overflow-hidden shadow-[0_0_60px_rgba(0,92,255,0.08)] ring-1 ring-inset ring-white/[0.03] backdrop-blur-xl relative" data-testid="planning-desktop">
        {/* Ambient corner glows */}
        <div className="pointer-events-none absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#005CFF]/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-emerald-500/[0.06] blur-3xl" />
        <div className="overflow-x-auto no-scrollbar relative">
          <div className="min-w-[720px]">
            <div className="grid bg-gradient-to-b from-white/[0.06] via-white/[0.02] to-transparent" style={{ gridTemplateColumns: "140px repeat(7, minmax(80px, 1fr))" }}>
              <div className="p-3 text-[10px] uppercase tracking-[0.2em] text-[#7CB2FF]/70 font-semibold border-b border-[#005CFF]/20 flex items-center gap-1.5">
                <span className="w-1 h-3 rounded-full bg-gradient-to-b from-[#005CFF] to-transparent" />
                Équipe
              </div>
              {days.map((d) => {
                const isToday = d.getTime() === today.getTime();
                return (
                  <div key={d.toISOString()} className={`relative p-3 text-center border-b border-l border-white/5 transition-colors ${isToday ? "bg-gradient-to-b from-[#005CFF]/20 via-[#005CFF]/8 to-transparent" : ""}`}>
                    {isToday && (
                      <span className="absolute top-0 left-3 right-3 h-[2px] bg-gradient-to-r from-transparent via-[#005CFF] to-transparent shadow-[0_0_12px_rgba(0,92,255,0.6)]" />
                    )}
                    <div className={`text-[10px] uppercase tracking-[0.18em] ${isToday ? "text-[#7CB2FF] font-semibold" : "text-white/45"}`}>{d.toLocaleDateString("fr-FR", { weekday: "short" })}</div>
                    <div className={`font-display font-bold text-lg tabular-nums ${isToday ? "text-white [text-shadow:0_0_18px_rgba(0,92,255,0.5)]" : "text-white/85"}`}>{d.getDate()}</div>
                  </div>
                );
              })}
            </div>
            {team.map((u) => (
              <div key={u.id} className="grid border-b border-white/[0.04] hover:bg-white/[0.015] transition-colors" style={{ gridTemplateColumns: "140px repeat(7, minmax(80px, 1fr))" }}>
                <div className="p-3 flex items-center gap-2">
                  <div
                    className="w-8 h-8 rounded-xl flex items-center justify-center text-[10px] font-bold shrink-0 ring-1 ring-inset transition-transform hover:scale-105"
                    style={{
                      background: `linear-gradient(135deg, ${(u.color || "#005CFF")}40, ${(u.color || "#005CFF")}15)`,
                      color: u.color || "#7CB2FF",
                      boxShadow: `0 0 14px ${(u.color || "#005CFF")}30`,
                      "--tw-ring-color": `${(u.color || "#005CFF")}50`,
                    }}
                  >
                    {(u.name || "?").split(" ").map(s => s[0]).join("").slice(0,2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs truncate font-medium">{u.name}</div>
                    {u.teams && u.teams.length > 0 && (
                      <div className="text-[8px] uppercase tracking-[0.2em] text-white/40 truncate">{u.teams.join(" · ")}</div>
                    )}
                  </div>
                </div>
                {days.map((d) => {
                  const dayIso = isoDay(d);
                  const leave = leaveOnDay(u.id, dayIso, leaves);
                  const iv = interventions
                    .filter((x) => (x.technician_id === u.id || (x.assignee_ids || []).includes(u.id)) && ivCoversDay(x, dayIso))
                    .sort((a, b) => {
                      const ta = (a.scheduled_date || "").slice(11, 16);
                      const tb = (b.scheduled_date || "").slice(11, 16);
                      if (ta !== tb) return ta.localeCompare(tb);
                      return (a.sort_order || 0) - (b.sort_order || 0);
                    });
                  const cellKey = `${u.id}|${dayIso}`;
                  const isCellDropOver = dropTarget === cellKey;
                  const emptyCell = iv.length === 0;
                  const leaveBg = leave ? "bg-emerald-500/[0.12]" : "";
                  const isSpanPreview = dragSpan && dragSpan.techId === u.id && dayIso >= dragSpan.from && dayIso <= dragSpan.to;
                  return (
                    <div
                      key={dayIso}
                      data-day-iso={dayIso}
                      data-tech-id={u.id}
                      className={`relative p-1 border-l border-white/5 min-h-[70px] transition-colors ${leaveBg} ${isCellDropOver && emptyCell ? "bg-[#005CFF]/15 outline outline-2 outline-[#005CFF]/60 -outline-offset-2" : ""} ${isSpanPreview ? "bg-[#005CFF]/20 outline outline-2 outline-[#005CFF]/70 -outline-offset-2" : ""}`}
                      onDragOver={(e) => { if (isAdmin && dragging && emptyCell && !leave) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropTarget !== cellKey) setDropTarget(cellKey); } }}
                      onDragLeave={() => { if (dropTarget === cellKey) setDropTarget(null); }}
                      onDrop={(e) => { if (emptyCell && !leave) { e.preventDefault(); onDropReassign(u.id, dayIso); } }}
                      data-testid={`cell-${u.id}-${dayIso}`}
                      title={leave ? `${LEAVE_LABEL[leave.type] || "Congé"} · ${leave.start_date} → ${leave.end_date}${leave.reason ? " — " + leave.reason : ""}` : undefined}
                    >
                      {leave && (
                        <button
                          onClick={() => isAdmin && setEditingLeave(leave)}
                          disabled={!isAdmin}
                          className="relative w-full h-6 mb-1 rounded-md border border-emerald-400/30 bg-gradient-to-r from-emerald-500/[0.18] via-emerald-500/[0.08] to-transparent hover:from-emerald-500/[0.28] hover:border-emerald-400/60 active:scale-[0.97] transition-all flex items-center gap-1.5 px-2 text-[10px] text-emerald-200 disabled:cursor-default overflow-hidden shadow-[inset_0_0_10px_rgba(16,185,129,0.08)]"
                          data-testid={`planning-leave-${u.id}-${dayIso}`}
                          title={isAdmin ? `${LEAVE_LABEL[leave.type] || "Congé"}${leave.reason ? " — " + leave.reason : ""} (cliquer pour modifier)` : undefined}
                        >
                          <span className="w-1 h-1 rounded-full bg-emerald-300 shadow-[0_0_6px_rgba(52,211,153,0.9)] animate-pulse shrink-0" />
                          <span className="text-[9px] uppercase tracking-[0.18em] text-emerald-300/90 font-bold shrink-0">{LEAVE_LABEL[leave.type] || "Congé"}</span>
                          {leave.reason && (
                            <span className="text-[9px] text-emerald-200/60 truncate italic min-w-0">
                              · « {leave.reason} »
                            </span>
                          )}
                        </button>
                      )}
                      {iv.map((x, idx) => {
                        const gapKey = `dgap-${u.id}-${dayIso}-${idx}`;
                        const gapOver = dropTarget === gapKey;
                        const color = ivColor(x, chantiersMap, u);
                        const chantierName = x.chantier_id ? chantiersMap?.[x.chantier_id]?.name : null;
                        const primaryLabel = chantierName || x.title;
                        const secondaryLabel = chantierName && x.title && x.title !== chantierName ? x.title : null;
                        return (
                          <div key={x.id}>
                            {isAdmin && (
                              <div
                                onDragOver={(e) => { if (dragging) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropTarget !== gapKey) setDropTarget(gapKey); } }}
                                onDragLeave={() => { if (dropTarget === gapKey) setDropTarget(null); }}
                                onDrop={(e) => { e.preventDefault(); const sameCell = interventions.find((y) => y.id === dragging)?.technician_id === u.id && interventions.find((y) => y.id === dragging)?.scheduled_date?.slice(0,10) === dayIso; if (sameCell) onDropReorder(u.id, dayIso, idx, iv); else onDropReassign(u.id, dayIso); }}
                                className={`h-1 rounded-full transition-all ${gapOver ? "bg-[#005CFF] h-1.5" : "bg-transparent"}`}
                                data-testid={`drop-dgap-${u.id}-${dayIso}-${idx}`}
                              />
                            )}
                            <ChantierHoverPreview
                              chantier={x.chantier_id ? chantiersMap?.[x.chantier_id] : null}
                              client={x.chantier_id && chantiersMap?.[x.chantier_id]?.client_id ? clientsMap?.[chantiersMap[x.chantier_id].client_id] : null}
                              currentIv={x}
                              intervention={x}
                              dayIso={dayIso}
                              actions={isAdmin ? {
                                onExtendLeft: () => extendIv(x, "left", u.id),
                                onExtendRight: () => extendIv(x, "right", u.id),
                                onDuplicate: () => setDuplicateTarget(x),
                                onDelete: () => smartDelete(x, dayIso),
                                deleteLabel: ivSpanDays(x) > 1
                                  ? `Retirer ce jour (J${ivDayIndex(x, dayIso)}/${ivSpanDays(x)})`
                                  : "Supprimer l'intervention",
                              } : null}
                              side="right"
                            >
                            <div
                                className={`w-full rounded-xl transition-all relative group overflow-hidden mb-1 ${dragging === x.id ? "opacity-40 scale-[0.97]" : "hover:-translate-y-0.5"}`}
                                style={{
                                  background: `linear-gradient(135deg, ${color}42 0%, ${color}18 55%, ${color}08 100%)`,
                                  borderLeft: `3px solid ${color}`,
                                  boxShadow: `inset 0 0 0 1px ${color}30, 0 0 22px -8px ${color}, 0 2px 4px rgba(0,0,0,0.35)`,
                                }}
                              >
                                {/* Neon scan line on top edge */}
                                <span className="pointer-events-none absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent 0%, ${color}FF 50%, transparent 100%)`, opacity: 0.6 }} />
                                {/* Bottom subtle glow */}
                                <span className="pointer-events-none absolute inset-x-0 bottom-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}55, transparent)` }} />
                                <button
                                  draggable={isAdmin}
                                  onDragStart={(e) => { if (!isAdmin) return; setDragging(x.id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", x.id); } catch (err) { console.debug("[planning-desktop] setData failed:", err?.message || err); } }}
                                  onDragEnd={() => { setDragging(null); setDropTarget(null); }}
                                  onClick={() => setEditingIv(x)}
                                  className={`w-full text-left px-2.5 py-2 relative ${isAdmin ? "cursor-grab" : ""}`}
                                  data-testid={`iv-desktop-${x.id}-${dayIso}`}
                                  title={primaryLabel}
                                >
                                  {/* Row 1 — heure + badges */}
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <span
                                      className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                                      style={{ background: color, boxShadow: `0 0 6px ${color}` }}
                                      aria-hidden
                                    />
                                    <span className="font-mono tabular-nums text-[12px] font-bold tracking-wider text-white">
                                      {new Date(x.scheduled_date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                                    </span>
                                    <CategorieBadge value={x.categorie} size="xs" showLabel={false} />
                                    {x.type === "depannage" && (
                                      <span className="text-[8px] font-black tracking-wider bg-amber-400 text-black px-1.5 py-0.5 rounded shadow-[0_0_10px_rgba(251,191,36,0.7)]">
                                        SAV
                                      </span>
                                    )}
                                    {ivSpanDays(x) > 1 && (
                                      <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/15 text-white/95 ml-auto font-semibold ring-1 ring-white/10">
                                        J{ivDayIndex(x, dayIso)}/{ivSpanDays(x)}
                                      </span>
                                    )}
                                  </div>
                                  {/* Row 2 — nom du chantier (info primaire) */}
                                  <div className="text-[13px] font-semibold leading-tight text-white truncate pr-3 [text-shadow:0_1px_2px_rgba(0,0,0,0.4)]">
                                    {primaryLabel}
                                  </div>
                                  {/* Row 3 — titre optionnel si différent du chantier */}
                                  {secondaryLabel && (
                                    <div className="text-[10px] text-white/55 truncate mt-0.5 pr-3 italic">
                                      {secondaryLabel}
                                    </div>
                                  )}
                                </button>
                                {x.chantier_id && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); nav(`/chantiers/${x.chantier_id}`); }}
                                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"
                                    title="Ouvrir la fiche chantier"
                                    data-testid={`iv-desktop-open-ch-${x.id}`}
                                  >
                                    <ArrowRight size={10} weight="bold" />
                                  </button>
                                )}
                              </div>
                            </ChantierHoverPreview>
                          </div>
                        );
                      })}
                      {isAdmin && iv.length > 0 && (
                        <div
                          onDragOver={(e) => { if (dragging) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (dropTarget !== `dgap-${u.id}-${dayIso}-${iv.length}`) setDropTarget(`dgap-${u.id}-${dayIso}-${iv.length}`); } }}
                          onDragLeave={() => { if (dropTarget === `dgap-${u.id}-${dayIso}-${iv.length}`) setDropTarget(null); }}
                          onDrop={(e) => { e.preventDefault(); const sameCell = interventions.find((y) => y.id === dragging)?.technician_id === u.id && interventions.find((y) => y.id === dragging)?.scheduled_date?.slice(0,10) === dayIso; if (sameCell) onDropReorder(u.id, dayIso, iv.length, iv); else onDropReassign(u.id, dayIso); }}
                          className={`h-2 rounded-full transition-all ${dropTarget === `dgap-${u.id}-${dayIso}-${iv.length}` ? "bg-[#005CFF] h-1.5" : "bg-transparent"}`}
                        />
                      )}
                      {(isAdmin || currentUser?.id === u.id) && iv.length === 0 && !leave && (
                        <button
                          onClick={() => setQuickTarget({ technicianId: u.id, technicianName: u.name, dateIso: dayIso })}
                          className="w-full h-full min-h-[52px] rounded-md border border-dashed border-white/10 hover:border-[#005CFF]/60 hover:bg-[#005CFF]/5 text-white/20 hover:text-[#7CB2FF] flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity active:scale-95"
                          title="Créer une intervention ici"
                          data-testid={`empty-cell-${u.id}-${dayIso}`}
                        >
                          <Plus size={14} weight="bold" />
                        </button>
                      )}
                      {(isAdmin || currentUser?.id === u.id) && iv.length > 0 && (
                        <button
                          onClick={() => setQuickTarget({ technicianId: u.id, technicianName: u.name, dateIso: dayIso })}
                          className="mt-1 w-full h-5 rounded border border-dashed border-white/10 hover:border-[#005CFF]/60 hover:bg-[#005CFF]/10 text-white/25 hover:text-[#7CB2FF] flex items-center justify-center transition-colors active:scale-95"
                          title="Ajouter une autre intervention ce jour"
                          data-testid={`add-more-cell-${u.id}-${dayIso}`}
                        >
                          <Plus size={11} weight="bold" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
            {team.length === 0 && (
              <div className="p-8 text-center text-white/40 text-sm">Aucun technicien{" "}<span className="text-white/30">(essaie de changer le filtre sous-équipe)</span></div>
            )}
          </div>
        </div>
      </div>

      <QuickInterventionDialog
        open={!!quickTarget}
        onOpenChange={(o) => !o && setQuickTarget(null)}
        technicianId={quickTarget?.technicianId}
        technicianName={quickTarget?.technicianName}
        dateIso={quickTarget?.dateIso}
        onCreated={onChange}
      />
      <EditInterventionDialog
        open={!!editingIv}
        onOpenChange={(o) => !o && setEditingIv(null)}
        intervention={editingIv}
        team={allTeam || team}
        chantiersById={chantiersMap}
        onChanged={onChange}
      />
      <LeaveEditDialog
        open={!!editingLeave}
        onOpenChange={(o) => !o && setEditingLeave(null)}
        leave={editingLeave}
        onChanged={onChange}
      />
      <DuplicateInterventionDialog
        open={!!duplicateTarget}
        onOpenChange={(o) => !o && setDuplicateTarget(null)}
        intervention={duplicateTarget}
        team={allTeam || team}
        onDuplicated={onChange}
      />
    </>
  );
}
